import { Box } from "@chakra-ui/react";
import { useState, useRef, useMemo, useEffect } from "react";
import TimezoneHeader from "../primitives/TimezoneHeader";
import DayHeaders from "./DayHeaders";
import TimeColumn from "../primitives/TimeColumn";
import { CalendarEvent, PositionedEvent } from "../types";
import CalendarGrid from "./CalendarGrid";
import { calculatePositionedEvents } from "../primitives/calendarLogic";
import { createMouseHandlers } from "../primitives/calendarHandlers";

interface CalendarProps {
  events: CalendarEvent[];
  onUpdate: (eventId: string, newStart: Date, newEnd: Date) => void;
  onCreate: (start: Date, end: Date) => void;
  onEventClick?: (event: CalendarEvent) => void;
  startDate: Date; // beginning of week
  timezone: string;
  editable: boolean;
  showWeekends?: boolean;
}

// Constants
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const SLOT_HEIGHT = 40; // px per hour
// These will be calculated based on props
const MIN_DURATION_MINUTES = 15;
const SHOW_15_MINUTES = false; // Show 15-minute increments
const TIME_COLUMN_WIDTH = 80; // px width for time labels

export default function Calendar({ events, onUpdate, onCreate, onEventClick, startDate, timezone, showWeekends = false }: CalendarProps) {
  const numDays = showWeekends ? 7 : 5; // Number of days in the calendar view
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [dragged, setDragged] = useState<PositionedEvent | null>(null);
  const [resizing, setResizing] = useState<PositionedEvent | null>(null);
  const [creating, setCreating] = useState<{ startY: number; dayIndex: number; startTime: Date } | null>(null);

  // Auto-scroll to 7AM on mount
  useEffect(() => {
    if (scrollContainerRef.current) {
      const sevenAmPosition = 7 * SLOT_HEIGHT;
      scrollContainerRef.current.scrollTop = sevenAmPosition;
    }
  }, []);

  const positionedEvents = useMemo(() => calculatePositionedEvents({
    events,
    startDate,
    numDays: numDays,
    slotHeight: SLOT_HEIGHT,
    timezone,
  }), [numDays, events, startDate, timezone]);

  const {
    handleMouseDown,
    handleResizeStart,
    handleMouseMove,
    handleMouseUp,
  } = createMouseHandlers({
    containerRef,
    numDays: numDays,
    slotHeight: SLOT_HEIGHT,
    minDurationMinutes: MIN_DURATION_MINUTES,
    startDate,
    timezone,
    dragged,
    resizing,
    creating,
    setDragged,
    setResizing,
    setCreating,
    onUpdate,
    onCreate,
  });

  return (
    <Box display="flex" flexDirection="column" borderWidth="1px" borderColor="gray.300" userSelect="none">
      {/* Fixed header row */}
      <Box display="flex" bg="white" borderBottomWidth="2px" borderBottomColor="gray.200" position="sticky" top={0} zIndex={10}>
        <TimezoneHeader timezone={timezone} timeColumnWidth={TIME_COLUMN_WIDTH} slotHeight={SLOT_HEIGHT} />
        <DayHeaders startDate={startDate} numDays={numDays} slotHeight={SLOT_HEIGHT} />
      </Box>

      {/* Scrollable content */}
      <Box
        ref={scrollContainerRef}
        display="flex"
        height="600px"
        overflow="auto"
      >
        <TimeColumn timeColumnWidth={TIME_COLUMN_WIDTH} slotHeight={SLOT_HEIGHT} hours={HOURS} />

        <CalendarGrid
          containerRef={containerRef}
          positionedEvents={positionedEvents}
          timezone={timezone}
          dragged={dragged}
          resizing={resizing}
          numDays={numDays}
          slotHeight={SLOT_HEIGHT}
          hours={HOURS}
          show15Minutes={SHOW_15_MINUTES}
          startDate={startDate}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onEventClick={onEventClick}
          onMouseDown={handleMouseDown}
          onResizeStart={handleResizeStart}
          canCreate={false} // Disable creation for now
          setCreating={setCreating}
        />
      </Box>
    </Box>
  );
}
